import React, { createContext, useState, useContext, useEffect } from "react";
import axiosInstance from "../axiosInstance";
import axios from "axios";

// Enable or disable debugging globally
const DEBUG = false;

const logDebug = (label, data) => {
  if (DEBUG) {
    console.groupCollapsed(
      `%c[DEBUG] ${label}`,
      "color: #3b82f6; font-weight: bold;",
    );
    console.log(data);
    console.groupEnd();
  }
};

/**
 * @typedef {object} ModuleAccess
 * @property {string} moduleAccessID
 * @property {string[]} moduleFeatureAccessIDs
 */

/**
 * @typedef {object} User
 * @property {string} status
 * @property {string} employeeName
 * @property {string} username
 * @property {string[]} authorities
 * @property {string} role
 * @property {string} companyName
 * @property {ModuleAccess[]} moduleAccess
 */

/**
 * @typedef {object} AuthorizationConfig
 * @property {string[]} [roles] - Required roles for access
 * @property {string} [module] - Required module for access
 * @property {string} [feature] - Required feature for access
 * @property {boolean} [requireAll] - If true, user must have ALL specified roles/features
 */

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    logDebug("Auth Provider mounted", "Starting session establishment...");

    const establishSessionOnLoad = async () => {
      try {
        // --- Step 1: Refresh the session token ---
        const tenantId = sessionStorage.getItem("tenantId");
        const productId = process.env.REACT_APP_PRODUCT_ID;

        logDebug("Attempting to refresh token with credentials", {
          tenantId,
          productId,
        });

        const refreshTokenResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/refresh-token`,
          null, // No request body is needed
          {
            headers: {
              "X-TenantID": tenantId,
              "X-ProductID": productId,
            },
            withCredentials: true, // IMPORTANT: This sends the HttpOnly refreshToken cookie
          },
        );

        const newJwtToken =
          refreshTokenResponse.headers["Authorization"] ||
          refreshTokenResponse.headers["authorization"];

        if (newJwtToken) {
          sessionStorage.setItem("jwtToken", newJwtToken);
          logDebug("New JWT Token stored in sessionStorage.", newJwtToken);
        } else {
          throw new Error("No JWT token received from refresh endpoint.");
        }

        // --- Step 2: Fetch user access rights with the new token ---
        logDebug("Token refreshed. Now fetching user access data...");

        const accessResponse = await axiosInstance.get("/api/access");

        logDebug("User Access Response", {
          status: accessResponse.status,
          data: accessResponse.data,
        });

        if (accessResponse.data) {
          setUser(accessResponse.data);
          logDebug("User session fully established.", accessResponse.data);
        } else {
          throw new Error("API /api/access did not return any user data.");
        }
      } catch (error) {
        logDebug("Session establishment failed", { error });
        console.error("Failed to establish session:", error.message);
        setUser(null);
        sessionStorage.removeItem("jwtToken");
      } finally {
        setIsLoading(false);
        logDebug("Initial loading process finished.");
      }
    };

    const tenantId = sessionStorage.getItem("tenantId");

    if (tenantId) {
      logDebug("Tenant ID found", "Proceeding to establish session.");
      establishSessionOnLoad();
    } else {
      logDebug("No Tenant ID found", "Skipping session establishment.");
      setIsLoading(false);
    }
  }, []);

  const login = (userData) => {
    setUser(userData);
    logDebug("Manual login successful", userData);
  };

  const logout = () => {
    // Frontend-only logout: no /api/logout endpoint exists, so just clear local state.
    setUser(null);
    sessionStorage.clear();
    window.location.href = "/";
  };

  // === ROLE-BASED ACCESS CONTROL ===
  const hasRole = (role) => user?.authorities?.includes(role) ?? false;
  const hasAuthority = (authority) =>
    user?.authorities?.includes(authority) ?? false;
  const hasAllAuthorities = (authorities) =>
    authorities.every((auth) => user?.authorities?.includes(auth));
  const hasAnyAuthority = (authorities) =>
    authorities.some((auth) => user?.authorities?.includes(auth));

  // === MODULE-BASED ACCESS CONTROL ===
  const canAccessModule = (moduleId) =>
    user?.moduleAccess?.some((module) => module.moduleAccessID === moduleId) ??
    false;

  const canAccessModuleFeature = (featureId) => {
    if (!user || !user.moduleAccess) return false;
    const allFeatureIds = user.moduleAccess.flatMap(
      (module) => module.moduleFeatureAccessIDs,
    );
    return allFeatureIds.includes(featureId);
  };

  const hasModuleAccess = (moduleId, featureId = null) => {
    if (!user || !user.moduleAccess) return false;
    const moduleEntry = user.moduleAccess.find(
      (mod) => mod.moduleAccessID === moduleId,
    );
    if (!moduleEntry) return false;
    if (!featureId) return true;
    return moduleEntry.moduleFeatureAccessIDs.includes(featureId);
  };

  // === UNIFIED AUTHORIZATION SYSTEM ===
  const isAuthorized = (config = {}) => {
    if (!user) return false;
    const { roles = [], module, feature, requireAll = false } = config;
    if (!roles.length && !module && !feature) return true;

    let roleCheck = true;
    let moduleCheck = true;

    if (roles.length > 0) {
      roleCheck = requireAll
        ? roles.every((role) => user.authorities?.includes(role))
        : roles.some((role) => user.authorities?.includes(role));
    }

    if (module || feature) {
      if (module && feature) {
        moduleCheck = hasModuleAccess(module, feature);
      } else if (feature) {
        moduleCheck = canAccessModuleFeature(feature);
      } else if (module) {
        moduleCheck = canAccessModule(module);
      }
    }
    return requireAll ? roleCheck && moduleCheck : roleCheck || moduleCheck;
  };

  // === UTILITY FUNCTIONS ===
  const getUserAuthorities = () => user?.authorities || [];
  const getUserRole = () => user?.role || "";

  const filterMenuItems = (menuItems) => {
    return menuItems.filter((item) => {
      if (item.auth) return isAuthorized(item.auth);
      if (item.roles || item.requiredModules || item.requiredFeature) {
        return isAuthorized({
          roles: item.roles,
          module: item.requiredModules?.[0],
          feature: item.requiredFeature,
        });
      }
      if (item.feature) return isAuthorized({ feature: item.feature });
      if (item.features)
        return item.features.some((feature) => isAuthorized({ feature }));
      return true;
    });
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    // Role-based functions
    hasRole,
    hasAuthority,
    hasAllAuthorities,
    hasAnyAuthority,
    // Module-based functions
    canAccessModule,
    canAccessModuleFeature,
    hasModuleAccess,
    // Unified authorization & utilities
    isAuthorized,
    filterMenuItems,
    getUserAuthorities,
    getUserRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
